import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCoachMode } from "./CoachModeContext";
import { fetchCoachHome, logCoachContact } from "./coachServices";
import { activeTeamOf, mailtoFor, plural, swingArchetypes, teamKey, teamNameOf } from "./coachUtils";
import { CoachPhoneHead, SavedRosterNotice, StatusLegend, SwingComposition, TeamSwitch } from "./coachPieces";
import { ATTENTION_ID, AttentionCard, ComplianceColumn, HomeKpis, RecentContactCard, RosterAnalysis, TeamSnapshotCard } from "./coachHomeCards";
import RosterModals from "./RosterModals";
import { greeting } from "../../ForBrands/brandDealUtils";
import { Btn, ErrorState, Loading, PageHeader } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";

// Coach Mode · Home (Figma 01 · Home; the Dashboard tab): the greeting with
// the team switch and roster actions (Manage roster, CacheShare, and Team
// Code, which opens the team code athletes join with), the KPI strip,
// roster analysis, recent athlete contact, team snapshot, Swing
// Composition, then the attention queue (the roster's live deals, team
// profile completion and a deal overview) next to the compliance summary.
// The full roster table lives on Team.
//
// On phones (Figma COACH MODE V2 mobile, 01 · Home 2406:745) the page keeps
// the same sections in the same order: the phone head (no eyebrow, the
// actions stacked full width), then one column 20px apart. Each card picks
// its phone layout itself (coachHomeCards.js, coachPieces.js).

// A reminder mail: one athlete in To, several in Bcc so no athlete sees
// another's address.
const reminderDraft = (item, emails) =>
  mailtoFor({ ...(emails.length === 1 ? { email: emails[0] } : { bcc: emails }), subject: item.title, body: `${item.meta}\n\n` });

const CoachHomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selection, absorbSelector, selectTeam } = useCoachMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [modal, setModal] = useState(null); // "code" | "roster"
  const scrolledKey = useRef(null);
  const phone = usePhoneTheme();

  const load = async (team, season) => {
    setLoading(true);
    try {
      const payload = await fetchCoachHome(team, season);
      setData(payload);
      absorbSelector(payload.selector, team);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your home view");
    } finally {
      setLoading(false);
    }
  };
  const reload = () => load(selection.team, selection.season);

  // Keyed on team AND league: one program can be several squads that share
  // a team id (17U / 16U / 15U), one per league.
  const selectedTeam = teamKey(selection.team);
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam, selection.season]);

  // The header mail badge links to /coach/home#attention. The queue only
  // renders once Home has loaded, so scroll to it then, once per
  // navigation (a click while on Home is a new location key).
  useEffect(() => {
    if (!data || location.hash !== `#${ATTENTION_ID}` || scrolledKey.current === location.key) return;
    scrolledKey.current = location.key;
    document.getElementById(ATTENTION_ID)?.scrollIntoView({ block: "start" });
  }, [data, location.hash, location.key]);

  const players = useMemo(() => data?.players || [], [data]);
  const playersById = useMemo(() => Object.fromEntries(players.map((p) => [p.athleteId, p])), [players]);
  const nameByEmail = useMemo(() => Object.fromEntries(players.filter((p) => p.email).map((p) => [p.email, `${p.name} ${p.surname}`])), [players]);
  const archetypes = useMemo(() => swingArchetypes(players), [players]);

  // "Notify athlete" / "Remind athletes": log a reminder for every athlete
  // on the item, so Recent athlete contact stays honest, then open one draft.
  const notify = async (item) => {
    const emails = item.athleteEmails || [];
    if (!emails.length) {
      toast.error("No athlete on this item has an email on file.");
      return;
    }
    setBusyId(item.id);
    try {
      await Promise.all(emails.map((email) => logCoachContact({ email, name: nameByEmail[email] || "", kind: "notify", athleteEmail: email })));
      window.location.href = reminderDraft(item, emails);
      reload();
    } catch (err) {
      toast.error(err.message || "Could not log the reminder");
    } finally {
      setBusyId(null);
    }
  };

  const message = async (c) => {
    try {
      await logCoachContact({ email: c.email, name: `${c.name} ${c.surname}`, kind: "message", athleteEmail: c.email });
      window.location.href = mailtoFor({ email: c.email, subject: `Checking in from ${teamNameOf(data.team) || "your coach"}` });
      reload();
    } catch (err) {
      toast.error(err.message || "Could not log the message");
    }
  };

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { coach, team, kpis, attention, compliance, contacts, snapshot, analysis } = data;
  const totals = team?.totals || {};
  const activeTeam = activeTeamOf(selection);
  // The team by the one display rule ("Oakland Soldiers 16U", coachUtils).
  const teamName = teamNameOf(team);
  const title = `${greeting()}, Coach ${coach?.lastName || coach?.firstName || ""}`.trim();
  const subtitle = `${teamName || "Your team"} · ${plural(totals.total ?? 0, "athlete")} on roster · ${totals.activated ?? 0} activated`;
  const teamSwitch = <TeamSwitch selection={selection} onSelect={selectTeam} disabled={loading} />;

  return (
    <div>
      {phone ? (
        // Home spaces the hero and the actions 20px apart (2406:778), Team
        // and Circle keep the head's 18px.
        <CoachPhoneHead
          className="!gap-5"
          title={title}
          sub={subtitle}
          subSize="m"
          switcher={teamSwitch}
          secondary={[activeTeam && { label: "Manage roster", onClick: () => setModal("roster") }, { label: "CacheShare", onClick: () => navigate("/coach/cacheshare") }]}
          primary={{ label: "Team Code", onClick: () => setModal("code") }}
        />
      ) : (
        <PageHeader
          align="center"
          eyebrow="Coach Mode"
          title={title}
          subtitle={subtitle}
          actions={
            <div className="flex flex-wrap items-center gap-6">
              {teamSwitch}
              {activeTeam && (
                <Btn variant="secondary" onClick={() => setModal("roster")}>
                  Manage roster
                </Btn>
              )}
              <span className="flex items-center gap-[10px]">
                <Btn variant="secondary" onClick={() => navigate("/coach/cacheshare")}>
                  CacheShare
                </Btn>
                <Btn onClick={() => setModal("code")}>Team Code</Btn>
              </span>
            </div>
          }
        />
      )}

      <div className={phone ? "flex flex-col gap-5 pt-5" : "flex flex-col gap-4 pt-7"}>
        {team?.rosterSnapshot && <SavedRosterNotice asOf={team.rosterSnapshot.asOf} />}
        <HomeKpis kpis={kpis} totals={totals} />
        <StatusLegend />
        <RosterAnalysis analysis={analysis} kpis={kpis} snapshot={snapshot} totals={totals} />
        <div className={phone ? "flex flex-col gap-5" : "grid gap-4 lg:grid-cols-2"}>
          <RecentContactCard contacts={contacts} playersById={playersById} archetypes={archetypes} teamName={teamName} onMessage={message} />
          <TeamSnapshotCard snapshot={snapshot} kpis={kpis} totals={totals} />
        </div>
        <SwingComposition players={players} teamName={teamName} />
        <div className={phone ? "flex flex-col gap-5" : "flex flex-col gap-5 lg:flex-row lg:items-start"}>
          <AttentionCard items={attention} busyId={busyId} onNotify={notify} />
          <ComplianceColumn compliance={compliance} />
        </div>
      </div>

      <RosterModals modal={modal} onClose={() => setModal(null)} team={activeTeam} players={players} onChanged={reload} />
    </div>
  );
};

export default CoachHomePage;
